import { useEffect, useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { LoaderSymbol } from '../../../components/loading';
import {
  useAdcUsers, useAdcAssignments, useSetAdcUsers, ADC_ROLE_LABEL, errMessage,
  type AdcRole, type AdcUser,
} from './adc';

interface Props {
  testMethodId: string;
  role: AdcRole;
  rowLabel: string;          // "Matrix / Test / Method" for the header
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Available ↔ Selected dual-grid picker — the legacy ALPACA "Employees" popup.
 * Two data grids (FullName / DisplayName / LabUser) with green + (add) on the
 * left and red − (remove) on the right; each grid has its own search, sort,
 * column filters and pagination. Save persists the Selected set for this
 * (TestMethod, role) to the legacy AnalysisDepartmentChain tables.
 */
export function UserPickerDialog({ testMethodId, role, rowLabel, onClose, onSaved }: Props) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const { data: users = [], isFetching: usersLoading } = useAdcUsers();
  const { data: assignments, isFetching: aLoading } = useAdcAssignments(testMethodId);
  const save = useSetAdcUsers();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState(false);
  const [availSearch, setAvailSearch] = useState('');
  const [selSearch, setSelSearch] = useState('');
  const [maxed, setMaxed] = useState(false);   // maximize / restore the dialog

  useEffect(() => {
    if (assignments && !ready) { setSelected(new Set(assignments[role] ?? [])); setReady(true); }
  }, [assignments, ready, role]);

  const byId = useMemo(() => {
    const m = new Map<string, AdcUser>();
    for (const u of users) m.set(u.oid, u);
    return m;
  }, [users]);

  const matches = (u: AdcUser, q: string) =>
    !q || [u.fullName, u.displayName].some((v) => (v ?? '').toLowerCase().includes(q));

  const available = useMemo(() => {
    const q = availSearch.trim().toLowerCase();
    return users.filter((u) => !selected.has(u.oid) && matches(u, q));
  }, [users, selected, availSearch]);

  const selectedUsers = useMemo(() => {
    const q = selSearch.trim().toLowerCase();
    return [...selected].map((id) => byId.get(id)).filter((u): u is AdcUser => !!u && matches(u, q));
  }, [selected, byId, selSearch]);

  const add = (id: string) => setSelected((s) => new Set(s).add(id));
  const remove = (id: string) => setSelected((s) => { const n = new Set(s); n.delete(id); return n; });

  const loading = usersLoading || aLoading || !ready;

  // Filter on EVERY column; only FullName is click-sortable (shows the sort
  // arrow, default ascending). DisplayName / LabUser have the filter chevron only.
  const baseCols: AppColumn<AdcUser>[] = [
    { field: 'fullName',    header: 'FullName',    minWidth: 130, text: (u) => u.fullName },
    { field: 'displayName', header: 'DisplayName', width: 120, sortable: false, text: (u) => u.displayName },
    {
      field: 'labUser', header: 'LabUser', width: 90, align: 'center', sortable: false,
      text: (u) => (u.labUser ? 'Yes' : 'No'),
      render: (u) => <input type="checkbox" className="adc-lab" checked={u.labUser} readOnly />,
    },
  ];

  const availCols: AppColumn<AdcUser>[] = [
    {
      field: '_add', header: '', width: 40, align: 'center', sortable: false, filterable: false,
      render: (u) => (
        <button className="adc-move add adc-cellbadge" title="Add" onClick={() => add(u.oid)}>
          ＋
        </button>
      ),
    },
    ...baseCols,
  ];
  const selCols: AppColumn<AdcUser>[] = [
    {
      field: '_del', header: '', width: 40, align: 'center', sortable: false, filterable: false,
      render: (u) => (
        <button className="adc-move del adc-cellbadge" title="Remove" onClick={() => remove(u.oid)}>
          −
        </button>
      ),
    },
    ...baseCols,
  ];

  const onSave = async () => {
    try {
      await save.mutateAsync({ testMethodId, role, employeeIds: [...selected] });
      // Shared success acknowledgement modal (CLAUDE.md §18.6 save flow) — the
      // green "Success … OK" box, not a top toast.
      await alert({ title: 'Success', message: `${ADC_ROLE_LABEL[role]} users updated.`, tone: 'success' });
      onSaved();
      onClose();
    } catch (e) { notify.error(errMessage(e)); }
  };

  return (
    <div className="adc-overlay" onMouseDown={onClose}>
      <div className={`adc-dialog adc-dialog-wide${maxed ? ' adc-dialog-max' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="adc-dialog-hdr">
          {/* LIMS titles this popup simply "Employees"; the test/role context is
              kept as a hover tooltip. */}
          <span title={`${ADC_ROLE_LABEL[role]} — ${rowLabel}`}><i className="ti ti-users" /> Employees</span>
          <span className="adc-dialog-acts">
            <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} adc-dialog-x`}
              title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
            <i className="ti ti-x adc-dialog-x" onClick={onClose} />
          </span>
        </div>

        <div className="adc-dialog-body">
          {loading ? (
            <div className="adc-state"><LoaderSymbol size={14} /> Loading users…</div>
          ) : (
            <div className="adc-dual">
              <div className="adc-pane">
                <div className="adc-search">
                  <i className="ti ti-search" />
                  <input placeholder="Search…" value={availSearch} onChange={(e) => setAvailSearch(e.target.value)} />
                  {availSearch && <i className="ti ti-x adc-search-x" onClick={() => setAvailSearch('')} />}
                </div>
                <AppDataGrid<AdcUser> rows={available} columns={availCols} getRowId={(u) => u.oid}
                  defaultPageSize={20} initialSort={{ key: 'fullName', dir: 'asc' }} emptyText="No users" />
              </div>

              <div className="adc-pane">
                <div className="adc-search">
                  <i className="ti ti-search" />
                  <input placeholder="Search…" value={selSearch} onChange={(e) => setSelSearch(e.target.value)} />
                  {selSearch && <i className="ti ti-x adc-search-x" onClick={() => setSelSearch('')} />}
                </div>
                <AppDataGrid<AdcUser> rows={selectedUsers} columns={selCols} getRowId={(u) => u.oid}
                  defaultPageSize={20} initialSort={{ key: 'fullName', dir: 'asc' }} emptyText="No users assigned" />
              </div>
            </div>
          )}
        </div>

        <div className="adc-dialog-ftr">
          <button className="adc-btn adc-btn-primary" onClick={() => void onSave()} disabled={loading || save.isPending}>
            {save.isPending ? 'Saving…' : 'OK'}
          </button>
          <button className="adc-btn adc-btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
